import type { FailedCreator } from "@unbox/api";
import { Mail01, XCircle } from "@untitledui/icons";
import { useMemo } from "react";
import { useCreatorsByIds } from "@/api/creator";
import { useVideosByIds } from "@/api/video";
import { Button } from "@/components/base/buttons/button";
import { DialogFooter, DialogHeader, DialogTitle, ScrollableDialogContent } from "@/components/ui/dialog";
import { translateError } from "@/lib/assurance/bulk-action-errors";
import { formatCreatorId } from "@/utils/formatters";

type BulkActionCompletion = {
  totalProcessed: number;
  totalFailed: number;
  failedCreators: FailedCreator[];
};

const isVideoBased = (actionType: string): boolean =>
  actionType === "demonetization_ticket" ||
  actionType === "takedown_request" ||
  actionType === "remove_from_ad_campaign";

const getTitle = (actionType: string): string => {
  switch (actionType) {
    case "contact_creator":
      return "Message summary";
    case "remove_from_collaboration":
      return "Removal summary";
    case "demonetization_ticket":
      return "Demonetization ticket summary";
    case "takedown_request":
      return "Takedown request summary";
    case "remove_from_ad_campaign":
      return "Ad campaign removal summary";
    default:
      return "Action summary";
  }
};

const contactSupport = () => {
  window.location.href = "mailto:support@unbox-commerce.com";
};

export const BulkActionResultsDialog = ({
  completion,
  actionType,
  onClose,
}: {
  completion: BulkActionCompletion;
  actionType: string;
  onClose: () => void;
}) => {
  const videoBasedAction = isVideoBased(actionType);

  const failedIds = useMemo(() => completion.failedCreators.map((fc) => fc.creatorId), [completion.failedCreators]);

  const { data: creatorMap } = useCreatorsByIds(videoBasedAction ? [] : failedIds);
  const { data: videoMap } = useVideosByIds(videoBasedAction ? failedIds : []);

  const getDisplayName = (id: string): string => {
    if (videoBasedAction) {
      const title = videoMap?.[id]?.videoTitle;
      return title && title.trim().length > 0 ? title : "Untitled video";
    }
    const creator = creatorMap?.[id];
    const name = creator?.displayName ?? creator?.username;
    // Fall back to formatting the raw id as `@username` (strips the CID- prefix) so newly-invited
    // creators that aren't in brand_creators yet still show a meaningful handle instead of the
    // literal string "Unknown creator".
    return name && name.trim().length > 0 ? name : formatCreatorId(id);
  };

  return (
    <ScrollableDialogContent className="sm:max-w-[600px]">
      <DialogHeader>
        <DialogTitle>{getTitle(actionType)}</DialogTitle>
      </DialogHeader>

      <div className="space-y-4 py-2">
        {completion.failedCreators.length > 0 && (
          <div className="max-h-60 overflow-y-auto overflow-x-hidden rounded-lg border border-secondary px-3 py-2 space-y-2">
            {completion.failedCreators.map((fc) => (
              <div key={fc.creatorId} className="flex items-start gap-2 text-sm min-w-0">
                <XCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <span className="text-primary font-medium break-words inline-block max-w-[60ch] truncate">
                    {getDisplayName(fc.creatorId)}
                  </span>
                  <p className="text-tertiary break-words mt-0.5">{translateError(fc.error)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <DialogFooter className="sm:justify-between">
        <Button type="button" color="primary" onClick={onClose}>
          Close
        </Button>
        {completion.totalFailed > 0 ? (
          <Button type="button" color="secondary" onClick={contactSupport}>
            <div className="flex gap-2">
              <Mail01 className="w-4 h-4" />
              Contact Support
            </div>
          </Button>
        ) : (
          <div />
        )}
      </DialogFooter>
    </ScrollableDialogContent>
  );
};
